import { executionForResponse } from '@/analytics/requests';
import {
  beginSearch, contextForPageRequest, contextForDeferredCount, isCurrentSearch,
  navigationMethod, resultCount, resultsPageForResponse, resultsPageFromCache,
} from '@/analytics/features/search-ui';
import {
  filterSnapshot, invalidFilterRanges, recordFilterApplyBlocked, recordFiltersApplied,
  rememberFilterOptions,
} from '@/analytics/features/filters';
import { newId } from '@/analytics/client';
import { beginAction, capturePageExecution, currentAccountScope, sameAccount } from '@/analytics/context';
import { message } from "antd";
import { 
    fetchProduct, 
    fetchProductsByIngredient, 
    fetchProductsByIngredientCount,
    fetchProductLists, 
    createProductList, 
    addProductsToList, 
    autoCompleteIngredientName, 
    fetchProductCategories,
    fetchFilterOptions,
} from "./service";
import { fetchPortfolioScopeInterests } from "../../Profile/service";
import { normalizePortfolioInterestRequestForApi } from "@/utils/portfolioInterestModes";

const PRODUCT_COUNT_POLL_INTERVAL_MS = 1000;
const PRODUCT_COUNT_MAX_POLLS = 60;
const waitForProductCount = (milliseconds: number) =>
    new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });

const DEFAULT_INTEREST_TOGGLES = {
    marketers: false,
    manufacturers: false,
    brands: false,
    ingredients: false,
    flavours: false,
    fragrances: false,
    packaging_types: false,
    packaging_materials: false,
};

const INTEREST_KEYS = [
    'marketers',
    'manufacturers',
    'brands',
    'ingredients',
    'flavours',
    'fragrances',
    'packaging_types',
    'packaging_materials',
];

const DEFAULT_INTEREST_ACTIONS = INTEREST_KEYS.reduce((acc: any, key) => {
    acc[key] = 'include';
    return acc;
}, {});

const normalizeInterestToggles = (interestToggles: any = {}) =>
    INTEREST_KEYS.reduce((acc: any, key) => {
        acc[key] = interestToggles[key] === true;
        return acc;
    }, {});

const normalizeInterestActions = (interestActions: any = {}) =>
    INTEREST_KEYS.reduce((acc: any, key) => {
        acc[key] = interestActions[key] === 'exclude' ? 'exclude' : 'include';
        return acc;
    }, {});

const normalizeInterestSelections = (interestSelections: any = {}) =>
    INTEREST_KEYS.reduce((acc: any, key) => {
        if (Array.isArray(interestSelections[key])) {
            acc[key] = [...new Set(interestSelections[key].map(Number))]
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        return acc;
    }, {});

const hasPortfolioScopeInterests = (scope: any = {}) =>
    (Array.isArray(scope.applications) && scope.applications.length > 0) ||
    (Array.isArray(scope.countries_of_origin) && scope.countries_of_origin.length > 0) ||
    (Array.isArray(scope.countries_of_sale) && scope.countries_of_sale.length > 0);

const Model = {
    namespace: 'packaging_search',
    state: {
        searchValue: '',
        appliedSearch: undefined,
        pageRequest: undefined,
        resultsPage: undefined,
        searchRequestToken: undefined,
        filterMetadata: {},
        filters: {},
        productsData: {
            pages: {},
            total: null,
            ingredientId: null,
            countKey: null,
            countStatus: 'idle',
        },
        currentPage: 1,
        pageSize: 10,
        productLists: [],
        selectedRows: [], // Will store product IDs instead of row indices
        ingredientSuggestions: [],
        packagingSuggestions: [],
        filterDrawerVisible: false,
        listModalVisible: false,
        productCategories: [],
        productCategoriesLoaded: false,
        filterOptions: {},
        filterOptionsLoaded: false,
        interestToggles: { ...DEFAULT_INTEREST_TOGGLES },
        interestActions: { ...DEFAULT_INTEREST_ACTIONS },
        interestSelections: {},
        viewMode: 'grid' as 'grid' | 'table'
    },
    effects: {
        *ingredientAutoComplete({ payload, callback }: any, { call, put }: any) {
            try {
                yield put({ type: 'setIngredientSuggestions', payload: [] });
                const response: Promise<any> = yield call(autoCompleteIngredientName, payload);
                yield put({ type: 'setIngredientSuggestions', payload: response });
            } catch (error) {
                console.error('Failed to fetch suggestions:', error);
            } finally {
                if (callback) callback();
            }
        },
        *fetchFilterOptions({ payload }: any, { call, put }: any) {
            try {
                const response: Promise<any> = yield call(fetchFilterOptions, payload);
                yield put({ type: 'setFilterOptions', payload: response });
            } catch (error) {
                console.error('Failed to fetch filter options:', error);
            }
        },
        *clearFilter({ payload, analytics }: any, { put, select }: any) {
            const currentState: Promise<any> = yield select((state: any) => state.packaging_search);
            const { filters } = currentState as any;
            
            // Create a new filters object without the specified filter keys
            const updatedFilters = { ...filters };
            
            // Handle payload as either a single key or an array of keys to clear
            const keysToRemove = Array.isArray(payload) ? payload : [payload];
            keysToRemove.forEach(key => {
                delete updatedFilters[key];
            });
            
            // Update the filters state
            yield put({
                type: 'updateState',
                payload: {
                    filters: updatedFilters
                }
            });
            
            // Trigger a search with the updated filters
            yield put({
                type: 'fetchProductsByIngredient',
        analytics,
                payload: {
                    current: 1 // Reset to first page when filters change
                }
            });
        },
    *fetchProductsByIngredient({ payload, analytics: intent }: any, { call, put, select }: any): Generator<any, void, any> {
      if (payload?.isPagination) {
        yield put({ type: 'changePage', payload: { page: payload.current, pageSize: payload.pageSize }, analytics: intent });
        return;
      }
      const searchState: any = yield select((state: any) => state.packaging_search);
      let profileState: any = yield select((state: any) => state.profile);
      const { filters: effectiveFilters, pageSize, interestToggles, interestActions, interestSelections } = searchState;

      const invalidRanges = invalidFilterRanges(effectiveFilters);
      if (invalidRanges.length) {
        recordFilterApplyBlocked('packaging_results', 'invalid_range', invalidRanges);
        message.error('Choose a valid filter range before searching.');
        return;
      }
      // Capture the explicit entry context before portfolio hydration can finish on a later page/session.
      const automatic = intent?.executionKind === 'automatic' || ['initial_load', 'deep_link'].includes(intent?.trigger);
      const action = intent?.action || (intent?.trigger && !automatic
        ? beginAction({ surface: 'packaging_results', context: intent?.context, trigger: intent.trigger }) : undefined);
      const execution = intent?.execution || (automatic
        ? capturePageExecution(intent?.context || { surface: 'packaging_results' }) : undefined);
      const token = newId();
      const account = currentAccountScope();
      yield put({ type: 'updateState', payload: { searchRequestToken: token } });
      const normalizedInterestToggles = normalizeInterestToggles(interestToggles);
      const apiPortfolioInterests = normalizePortfolioInterestRequestForApi({
        currentUser: profileState?.userData,
        interestToggles: normalizedInterestToggles,
        interestActions: normalizeInterestActions(interestActions),
        interestSelections: normalizeInterestSelections(interestSelections),
      });
      let productScopeInterests = profileState?.portfolioScopeInterests;
      if (!profileState?.interestListsLoaded?.portfolioScope) {
        productScopeInterests = yield call(fetchPortfolioScopeInterests);
        const latest: any = yield select((state: any) => state.packaging_search);
        if (latest.searchRequestToken !== token || (account && !sameAccount(account, currentAccountScope()))) return;
        yield put({ type: 'profile/setPortfolioScopeInterests', payload: productScopeInterests });
      }
      if (!Object.keys(effectiveFilters).length && !Object.values(normalizedInterestToggles).some(Boolean) && !hasPortfolioScopeInterests(productScopeInterests)) {
        recordFilterApplyBlocked('packaging_results', 'empty_criteria');
        return;
      }
      const size = payload?.pageSize || pageSize;
      const params = {
        take: size, skip: 0,
        filters: effectiveFilters,
        interestToggles: apiPortfolioInterests.interestToggles,
        interestActions: apiPortfolioInterests.interestActions,
        interestSelections: apiPortfolioInterests.interestSelections,
      };
      let metadata = rememberFilterOptions(searchState.filterMetadata, 'application_ids',
        [...(searchState.filterOptions?.applications || []), ...(productScopeInterests?.applications || [])]);
      metadata = rememberFilterOptions(metadata, 'product_category_ids', searchState.filterOptions?.product_categories || searchState.productCategories);
      metadata = rememberFilterOptions(metadata, 'sub_category_ids', searchState.filterOptions?.sub_categories);
      const criteria = filterSnapshot(params, metadata);
      const run = beginSearch({ surface: 'packaging_results', params, criteria,
        trigger: intent?.trigger, context: intent?.context, action,
        execution, executionKind: intent?.executionKind, workflowType: 'product_discovery' });
      const pageRequest = contextForPageRequest(run, { page: 1, pageSize: size, phase: 'submit', trigger: intent?.trigger });
      if (intent?.trigger === 'apply_filters') recordFiltersApplied({ surface: 'packaging_results',
        criteria, context: { ...run.search, ...run.action }, reason: intent.applyReason });
      yield put({ type: 'clearProductsData' });
      yield put({ type: 'updateState', payload: { appliedSearch: run, pageRequest, pageSize: size, resultsPage: undefined } });
      try {
        const response: any = yield call(fetchProductsByIngredient, { ...params }, pageRequest.analyticsContext);
        const latest: any = yield select((state: any) => state.packaging_search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        const facts = resultCount(response.total, response.countStatus);
        const page = resultsPageForResponse(run, pageRequest, response, facts);
        yield put({ type: 'setProductsForPage', payload: {
          page: 1, data: response.data, total: response.total, countKey: response.countKey,
          countStatus: response.countStatus, ingredientId: response.ingredient_id,
          enterpriseSearch: Boolean((params as any).module_filters && Object.keys((params as any).module_filters).length),
          runId: run.id, pageSize: size, analytics: page,
        } });
        yield put({ type: 'updateState', payload: { resultsPage: page, pageRequest: undefined } });
        yield put({ type: 'resetPackagingSuggestion' });
        if (response.countStatus === 'pending' && response.countKey) yield put({
          type: 'fetchProductsByIngredientCount', payload: { countKey: response.countKey,
            run, execution: executionForResponse(response),
            enterpriseSearch: Boolean((params as any).module_filters && Object.keys((params as any).module_filters).length) },
        });
      } catch (error) {
        const latest: any = yield select((state: any) => state.packaging_search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        yield put({ type: 'updateState', payload: { pageRequest: undefined } });
        message.error('Failed to fetch products');
      }
    },
    *changePage({ payload, analytics: intent }: any, { call, put, select }: any): Generator<any, void, any> {
      const state: any = yield select((state: any) => state.packaging_search);
      const run = state.appliedSearch;
      const page = payload.page;
      const size = payload.pageSize || state.pageSize;
      const sizeChanged = size !== state.pageSize;
      if (!Number.isSafeInteger(page) || page < 1 || !Number.isSafeInteger(size) || size < 1) return;
      if ((page === state.currentPage && !sizeChanged && !state.pageRequest && intent?.trigger !== 'retry') ||
          (state.pageRequest?.page === page && state.pageRequest?.pageSize === size)) return;
      if (!run) {
        // An already populated pre-migration cache stays usable; no attributed view is fabricated.
        if (state.productsData.pages[page]) yield put({ type: 'updateState', payload: { currentPage: page, resultsPage: undefined } });
        return;
      }
      if (!isCurrentSearch(run, run)) return;
      const pageRequest = contextForPageRequest(run, { page, pageSize: size,
        previousPage: state.currentPage, phase: 'page', trigger: intent?.trigger,
        action: intent?.action, execution: intent?.execution,
        navigationMethod: intent?.navigationMethod || navigationMethod(state.currentPage, page, sizeChanged) });
      const cached = state.productsData.pages[page];
      if (!sizeChanged && cached?.runId === run.id && cached.pageSize === size && intent?.trigger !== 'retry') {
        const view = cached.analytics ? resultsPageFromCache({ ...cached.analytics,
          ...resultCount(state.productsData.total, state.productsData.countStatus) }, pageRequest) : undefined;
        yield put({ type: 'updateState', payload: { currentPage: page, resultsPage: view, pageRequest: undefined } });
        return;
      }
      yield put({ type: 'updateState', payload: { pageRequest } });
      try {
        const params = { ...run.params, take: size, skip: (page - 1) * size, countKey: state.productsData.countKey };
        const response: any = yield call(fetchProductsByIngredient, params, pageRequest.analyticsContext);
        const latest: any = yield select((state: any) => state.packaging_search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        if (sizeChanged) yield put({ type: 'updateState', payload: { productsData: { ...latest.productsData, pages: {} }, pageSize: size } });
        const view = resultsPageForResponse(run, pageRequest, response, resultCount(response.total, response.countStatus));
        yield put({ type: 'setProductsForPage', payload: { page, data: response.data, total: response.total,
          countKey: response.countKey, countStatus: response.countStatus, runId: run.id, pageSize: size, analytics: view } });
        yield put({ type: 'updateState', payload: { resultsPage: view, pageRequest: undefined } });
        if (response.countStatus === 'pending' && response.countKey &&
            (latest.productsData.countKey !== response.countKey || latest.productsData.countStatus !== 'pending')) {
          yield put({ type: 'fetchProductsByIngredientCount', payload: { countKey: response.countKey,
            run, execution: executionForResponse(response), enterpriseSearch: state.productsData.enterpriseSearch } });
        }
      } catch (error) {
        const latest: any = yield select((state: any) => state.packaging_search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        yield put({ type: 'updateState', payload: { pageRequest: undefined } });
        message.error('Failed to fetch products for page');
      }
    },
    *fetchProductsByIngredientCount({ payload }: any, { call, put, select }: any): Generator<any, void, any> {
      const { countKey, run, execution } = payload || {};
      if (!countKey) return;
      try {
        for (let attempt = 0; attempt < PRODUCT_COUNT_MAX_POLLS; attempt += 1) {
          const before: any = yield select((state: any) => state.packaging_search);
          if (before.productsData.countKey !== countKey || (run && !isCurrentSearch(run, before.appliedSearch))) return;
          const response: any = yield call(fetchProductsByIngredientCount,
            { countKey }, contextForDeferredCount(execution, run));
          const current: any = yield select((state: any) => state.packaging_search);
          if (current.productsData.countKey !== countKey || (run && !isCurrentSearch(run, current.appliedSearch))) return;
          if (response.countStatus === 'pending') {
            yield call(waitForProductCount, PRODUCT_COUNT_POLL_INTERVAL_MS);
            continue;
          }
          const count = resultCount(response.total, response.countStatus);
          yield put({ type: 'setProductSearchCount', payload: { countKey, total: count.totalCount, countStatus: count.countStatus } });
          return;
        }
        throw new Error('Product count did not finish before the polling deadline');
      } catch (error) {
        const current: any = yield select((state: any) => state.packaging_search);
        if (current.productsData.countKey !== countKey || (run && !isCurrentSearch(run, current.appliedSearch))) return;
        yield put({ type: 'setProductSearchCount', payload: { countKey, total: null, countStatus: 'unavailable' } });
      }
    },
        *fetchProduct({ payload, callback }: any, { call, put, select }: any) {
            const account = currentAccountScope();
            const searchState: Promise<any> = yield select((state: any) => state.packaging_search)
            const { productsData } = searchState as any;

            const isViewed = Object.values(productsData.pages).some((pageData: any) => {
                return pageData.data.some((product: any) => 
                    product.id === payload.id && product.viewed
                );
            });

            // If already viewed, do nothing
            if (isViewed) {
                if (callback) callback();
                return;
            }

            const product: Promise<any> = yield call(fetchProduct, payload)
            if (account && !sameAccount(account, currentAccountScope())) return;
            yield put({ type: 'mergeProductDetails', payload: product });

            if (callback) callback()
        },
        *fetchProductLists(_: any, { call, put }: any) {
            const response: Promise<any> = yield call(fetchProductLists)
            yield put({
                type: 'setProductLists',
                payload: response
            })
        },
        *createProductList({ payload, callback }: any, { call, put, select }: any) {
            try {
                const response: Promise<any> = yield call(createProductList, payload)
                const existingProductLists: Promise<any> = yield select((state: any) => state.packaging_search.productLists)

                let newProductLists = [...(existingProductLists as any), {
                    id: (response as any).listId,
                    name: payload.name,
                    created_at: new Date().toISOString(),
                }]

                newProductLists = newProductLists.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

                yield put({
                    type: 'setProductLists',
                    payload: newProductLists
                })
                if (callback) callback()

            } catch (error: any) {
                if (callback) callback()
                message.error(error.response.data.message)
            }

        },
        *addProductsToList({ payload, callback }: any, { call, select, put }: any) {
            const { listId } = payload
            const { selectedRows } = yield select((state: any) => state.packaging_search)

            const response: Promise<any> = yield call(addProductsToList, {
                listId,
                productIds: selectedRows,
                ingredientId: payload.ingredientId,
                packagingType: payload.packagingType,
                countryOfOrigin: payload.countryOfOrigin,
                countriesOfSale: payload.countriesOfSale,
                category: payload.category
            })

            // Update productsData in the state with unobfuscated product data from response
            if ((response as any)?.success && (response as any)?.products) {
                let searchState: Promise<any> = yield select((state: any) => state.packaging_search);
                const { productsData } = searchState as any;

                // Update product data in all pages where they exist
                const updatedPages = Object.entries(productsData.pages).reduce((acc: any, [pageNum, pageData]: any) => {
                    const updatedData = pageData.data.map((product: any) => {
                        const updatedProduct = (response as any).products.find((p: any) => p.id === product.id);
                        if (updatedProduct) {
                            return {
                                ...updatedProduct,
                                countries_of_sale: updatedProduct.countries_of_sale.map((c: any) => c.name).join(", "),
                                marketed_by: updatedProduct.marketer.name,
                                viewed: true
                            };
                        }
                        return product;
                    });

                    acc[pageNum] = {
                        ...pageData,
                        data: updatedData
                    };
                    return acc;
                }, {});

                yield put({
                    type: 'updateState',
                    payload: {
                        productsData: {
                            ...productsData,
                            pages: updatedPages
                        }
                    }
                });
            }

            if (callback) {
                callback(response)
            }

        },
        *fetchProductCategories(_: any, { call, put }: any) {
            const response: Promise<any> = yield call(fetchProductCategories)

            yield put({
                type: 'setProductCategories',
                payload: response
            })
        }
    },
    reducers: {
        mergeProductDetails(state: any, { payload: product }: any) {
            const pages = Object.fromEntries(Object.entries(state.productsData.pages).map(([page, entry]: any) => [page, {
                ...entry,
                data: entry.data.map((row: any) => row.id !== product.id ? row : {
                    ...row, ...product,
                    countries_of_sale: Array.isArray(product.countries_of_sale)
                        ? product.countries_of_sale.map((country: any) => country.name).join(', ')
                        : product.countries_of_sale,
                    marketed_by: product.marketer?.name,
                    manufactured_by: product.manufacturer?.name,
                    viewed: true,
                }),
            }]));
            return { ...state, productsData: { ...state.productsData, pages } };
        },
        updateState(state: any, { payload }: any) {
            return {
                ...state,
                ...payload
            }
        },
    setProductsForPage(state: any, { payload }: any) {
      const { page, data, total, countKey, countStatus } = payload;
      const nextTotal = total !== undefined ? total : state.productsData.total;
      return { ...state, currentPage: page, productsData: { ...state.productsData,
        pages: { ...state.productsData.pages, [page]: { ...state.productsData.pages[page],
          data, total: nextTotal,
          ...(payload.runId ? { runId: payload.runId, pageSize: payload.pageSize, analytics: payload.analytics } : {}),
        } }, total: nextTotal,
        countKey: countKey !== undefined ? countKey : state.productsData.countKey,
        countStatus: countStatus || state.productsData.countStatus,
        ...(payload.enterpriseSearch !== undefined ? { enterpriseSearch: payload.enterpriseSearch } : {}),
        ...(payload.ingredientId !== undefined ? { ingredientId: payload.ingredientId } : {}),
      } };
    },
        setProductSearchCount(state: any, { payload }: any) {
            if (state.productsData.countKey !== payload.countKey) {
                return state;
            }

            const pages = Object.entries(state.productsData.pages).reduce((acc: any, [page, pageData]: any) => {
                acc[page] = {
                    ...pageData,
                    total: payload.total,
                };
                return acc;
            }, {});

            return {
                ...state,
        resultsPage: state.resultsPage ? { ...state.resultsPage, ...resultCount(payload.total, payload.countStatus) } : undefined,
                productsData: {
                    ...state.productsData,
                    pages,
                    total: payload.total,
                    countStatus: payload.countStatus,
                },
            };
        },
        setProductLists(state: any, { payload }: any) {
            return {
                ...state,
                productLists: payload,
            };
        },
        addToSelectedRows(state: any, { payload }: any) {
            // payload is productId
            if (!state.selectedRows.includes(payload)) {
                return {
                    ...state,
                    selectedRows: [...state.selectedRows, payload]
                }
            }
            return state
        },
        removeFromSelectedRows(state: any, { payload }: any) {
            return {
                ...state,
                selectedRows: state.selectedRows.filter((id: any) => id !== payload)
            }
        },
        addAllProductsToSelectedRows(state: any) {
            const { currentPage, productsData } = state
            const currentPageData = productsData.pages[currentPage]?.data || []
            const allIds = currentPageData.map((item: any) => item.id)
            const newSelectedRows = Array.from(new Set([...state.selectedRows, ...allIds]))
            return {
                ...state,
                selectedRows: newSelectedRows
            }
        },
        clearSelectedRows(state: any) {
            return {
                ...state,
                selectedRows: []
            }
        },
        setIngredientSuggestions(state: any, { payload }: any) {
            return {
                ...state,
                ingredientSuggestions: payload
            }
        },
        setPackagingSuggestions(state: any, { payload }: any) {
            return {
                ...state,
                packagingSuggestions: payload
            }
        },
        resetIngredientSuggestion(state: any) {
            return {
                ...state,
                ingredientSuggestions: []
            }
        },
        resetPackagingSuggestion(state: any) {
            return {
                ...state,
                packagingSuggestions: []
            }
        },
        updateSearchValue(state: any, { payload }: any) {
            return {
                ...state,
                searchValue: payload
            };
        },
        updateFilters(state: any, { payload }: any) {
            const cleanedFilters = Object.entries(payload).reduce((acc: any, [key, value]) => {
                if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
                    acc[key] = value;
                }
                return acc;
            }, {});
            
            return {
                ...state,
                filters: cleanedFilters
            }
        },
        updateInterestToggle(state: any, { payload }: any) {
            const key = payload?.key;
            if (!INTEREST_KEYS.includes(key)) return state;

            const interestSelections = normalizeInterestSelections(state.interestSelections);
            const currentInterestActions = normalizeInterestActions(state.interestActions);
            delete interestSelections[key];

            return {
                ...state,
                interestToggles: {
                    ...normalizeInterestToggles(state.interestToggles),
                    [key]: payload.value === true,
                },
                interestActions: {
                    ...currentInterestActions,
                    [key]: payload.action === 'include' || payload.action === 'exclude'
                        ? payload.action
                        : currentInterestActions[key],
                },
                interestSelections,
            };
        },
        updateInterestSelection(state: any, { payload }: any) {
            const key = payload?.key;
            if (!INTEREST_KEYS.includes(key)) return state;

            return {
                ...state,
                interestToggles: {
                    ...normalizeInterestToggles(state.interestToggles),
                    [key]: true,
                },
                interestActions: {
                    ...normalizeInterestActions(state.interestActions),
                    [key]: 'include',
                },
                interestSelections: {
                    ...normalizeInterestSelections(state.interestSelections),
                    [key]: [...new Set<number>((payload.selectedIds || []).map(Number))]
                        .filter((id: number) => Number.isInteger(id) && id > 0),
                },
            };
        },
        resetFilters(state: any) {
            return {
                ...state,
        appliedSearch: undefined, pageRequest: undefined, resultsPage: undefined, searchRequestToken: undefined, filterMetadata: {},
                filters: {},
                interestToggles: { ...DEFAULT_INTEREST_TOGGLES },
                interestActions: { ...DEFAULT_INTEREST_ACTIONS },
                interestSelections: {},
                searchValue: '',
                productsData: {
                    pages: {},
                    total: null,
                    ingredientId: null,
                    countKey: null,
                    countStatus: 'idle',
                },
                currentPage: 1
            }
        },
        toggleFilterDrawer(state: any) {
            return {
                ...state,
                filterDrawerVisible: !state.filterDrawerVisible
            }
        },
        toggleListModal(state: any) {
            return {
                ...state,
                listModalVisible: !state.listModalVisible
            }
        },
        setProductCategories(state: any, { payload }: any) {
            return {
                ...state,
                productCategories: payload,
                productCategoriesLoaded: true
            }
        },
        setFilterOptions(state: any, { payload }: any) {
            return {
                ...state,
                filterOptions: payload,
                filterOptionsLoaded: true
            };
        },
        resetAll(state: any) {
            return {
                ...state,
        appliedSearch: undefined, pageRequest: undefined, resultsPage: undefined, searchRequestToken: undefined, filterMetadata: {},
                searchValue: '',
                filters: {},
                interestToggles: { ...DEFAULT_INTEREST_TOGGLES },
                interestActions: { ...DEFAULT_INTEREST_ACTIONS },
                interestSelections: {},
                productsData: {
                    pages: {},
                    total: null,
                    ingredientId: null,
                    countKey: null,
                    countStatus: 'idle',
                },
                selectedRows: [],
                ingredientSuggestions: [],
                currentPage: 1
            }
        },
        clearProductsData(state: any) {
            return {
                ...state,
        appliedSearch: undefined, pageRequest: undefined, resultsPage: undefined, searchRequestToken: undefined,
                productsData: {
                    pages: {},
                    total: null,
                    ingredientId: null,
                    countKey: null,
                    countStatus: 'idle',
                },
                currentPage: 1
            }
        },
        setViewMode(state: any, { payload }: any) {
            return {
                ...state,
                viewMode: payload
            }
        }
    }
}

export default Model;
